/**
 * ==========================================================================
 *  Who is talking, and when.
 *
 *  Seven people have something to say during a run: Hannu wants wood, Kimmo
 *  swears at the glass and at the dark, somebody in the tent yells back,
 *  Mäysä explains the door, the cook offers breakfast, Kempf explains the
 *  sauna, and the porukka on the hall floor asks for a glass. This owns all
 *  of their timers and decides which sentence the HUD is showing.
 *
 *  Two rules run through it. Every line is scheduled off the *simulation*
 *  step rather than off a rendered frame, so two machines playing back one
 *  replay hear it in the same place. And nothing here can touch the
 *  simulation: a shout is a noise and a line of text, and the slow that came
 *  with the glass was applied by `RunSimulation` long before anybody reacted
 *  to it.
 *
 *  `current` is a strict priority list rather than a queue - see the reasons
 *  written into it, which are about who is standing in front of you.
 * ==========================================================================
 */

import { BLACKOUT_OHO_DELAY, BLACKOUT_SHOUT_DELAY, GLASS_SHOUT_DELAY } from '../config/audio';
import { HANNU } from '../config/gameplay';
import type { SpokenLine } from '../config/dialogue';
import {
  KEMPF_LINE,
  KIMMO_OHO_TEXT,
  KOKKI_LINE,
  MAYSA_DONE_LINE,
  MAYSA_LINE,
  PORUKKA_LINE,
  RUN_START_LINES,
  TELTTA_LINE,
} from '../config/dialogue';
import type { AudioManager } from '../audio/AudioManager';
import { HannuShoutGate } from './HannuShoutGate';

export class ShoutDirector {
  /** Non-null while Hannu is mid-sentence. */
  private hannuUntil = 0;
  private readonly hannuGate = new HannuShoutGate();
  /** Kimmo's opinion of the glass, a beat after his fist went through it. */
  private glassDueAt: number | null = null;
  /**
   * Non-null while the cook at the muurikka is mid-sentence, and `kokkiSeen`
   * once he has said it. He gets one line a run: he is offering breakfast, not
   * calling the register, and a man who says it again every time you come back
   * within fifteen metres is a man nobody wants to run past.
   */
  private kokkiUntil = 0;
  private kokkiSeen = false;
  /**
   * Mäysä's doorway reminder fires once per run. His second line replaces it
   * when the breakfast plate fills.
   */
  private maysaUntil = 0;
  private maysaSeen = false;
  /**
   * And for the porukka in the juhlasali, who get one line between the seven
   * of them.
   *
   * `porukkaSeen` is doing slightly more work than the other two flags. The
   * trigger is the `LAN_ROOM_ENTRY` split rather than a place on the floor, so
   * it could only fire twice if the checkpoint were stamped twice - which it
   * cannot be - but the flag is here anyway, because what the other two mean
   * by it is "he has been told", and being told is a thing that happens once
   * whatever sets it off.
   */
  private porukkaUntil = 0;
  private porukkaSeen = false;
  /**
   * And for Kempf at the sauna door - a window and no flag, which makes him
   * the only one of the five who may say his line twice.
   *
   * The other four are told-once. This one is not an invitation, it is an
   * *answer* - the door was tried, and the next press is a second question. So
   * there is nothing latching him, and leaning on the handle four times gets
   * the sentence four times. See `KEMPF_LINE`.
   */
  private kempfUntil = 0;
  /**
   * When the sauna door was last tried, which is what the wave at it is timed
   * off. Render-only: nothing downstream of this stamp can move a collider.
   */
  private kempfGreetedAt: number | null = null;
  /** Kimmo noticing the dark, and the tent yelling back a beat later. */
  private ohoDueAt: number | null = null;
  private telttaDueAt: number | null = null;
  private telttaUntil = 0;

  constructor(private readonly audio: AudioManager) {}

  /** A fresh run: nobody has said anything and nobody has been seen. */
  reset(): void {
    this.hannuUntil = 0;
    this.hannuGate.reset();
    this.glassDueAt = null;
    this.kokkiUntil = 0;
    this.kokkiSeen = false;
    this.maysaUntil = 0;
    this.maysaSeen = false;
    this.porukkaUntil = 0;
    this.porukkaSeen = false;
    this.kempfUntil = 0;
    this.kempfGreetedAt = null;
    this.ohoDueAt = null;
    this.telttaDueAt = null;
    this.telttaUntil = 0;
  }

  /** Seconds since the sauna door was tried, or null if nobody has tried it. */
  greetingAge(simulationTime: number): number | null {
    return this.kempfGreetedAt === null ? null : simulationTime - this.kempfGreetedAt;
  }

  /** Whether the porukka has been spoken to, which is what puts them in the HUD. */
  get sawPorukka(): boolean {
    return this.porukkaSeen;
  }

  private say(line: SpokenLine, simulationTime: number): number {
    this.audio.speak({ id: line.id, text: line.text, cue: line.cue, ...line.voice });
    return simulationTime + line.duration;
  }

  /**
   * The cable came out. Let the audible power-off sound finish before Kimmo
   * reacts and the tent replies - see `BLACKOUT_OHO_DELAY`.
   */
  powerCut(simulationTime: number): void {
    this.ohoDueAt = simulationTime + BLACKOUT_OHO_DELAY;
  }

  /** A fist through the pirtti's window; the opinion of it follows a beat later. */
  glassBroke(simulationTime: number): void {
    this.glassDueAt = simulationTime + GLASS_SHOUT_DELAY;
  }

  /**
   * The sauna door, tried. The hand restarts wherever the last one had got to -
   * a man asked twice greets you twice.
   */
  saunaTried(simulationTime: number): void {
    this.kempfUntil = this.say(KEMPF_LINE, simulationTime);
    this.kempfGreetedAt = simulationTime;
  }

  /** The plate is full, so Mäysä has nothing left to explain and says so. */
  breakfastDone(simulationTime: number): void {
    this.maysaUntil = this.say(MAYSA_DONE_LINE, simulationTime);
  }

  /** Kimmo is between the porch's walls with an empty plate. Once per run. */
  seeMaysa(simulationTime: number): void {
    if (this.maysaSeen) return;
    this.maysaSeen = true;
    this.maysaUntil = this.say(MAYSA_LINE, simulationTime);
  }

  /** Kimmo is inside the cook's radius, on the cook's side of the porch. */
  seeKokki(simulationTime: number): void {
    if (this.kokkiSeen) return;
    this.kokkiSeen = true;
    this.kokkiUntil = this.say(KOKKI_LINE, simulationTime);
  }

  /** The juhlasali's doorway is behind him and the sima is still in its glass. */
  enterHall(simulationTime: number): void {
    if (this.porukkaSeen) return;
    this.porukkaSeen = true;
    this.porukkaUntil = this.say(PORUKKA_LINE, simulationTime);
  }

  /** Whether anybody has been talked over lately. Used to hold Hannu back. */
  private get blackoutTalking(): boolean {
    return this.ohoDueAt !== null || this.telttaDueAt !== null;
  }

  /**
   * One simulation step: anything that came due fires, and Hannu gets his turn
   * if the blackout has finished having its say.
   */
  step(simulationTime: number, hannuShouted: boolean): void {
    if (this.ohoDueAt !== null && simulationTime >= this.ohoDueAt) {
      this.ohoDueAt = null;
      this.audio.play('kimmo-oho');
      // Start from actual playback so fixed-step rounding cannot overlap the clips.
      this.telttaDueAt = simulationTime + BLACKOUT_SHOUT_DELAY - BLACKOUT_OHO_DELAY;
    }

    // The yell out of the dark behind him, a beat late. It needs no `seen` flag
    // of its own, because the thing that schedules it can only happen once.
    if (this.telttaDueAt !== null && simulationTime >= this.telttaDueAt) {
      this.telttaDueAt = null;
      this.telttaUntil = this.say(TELTTA_LINE, simulationTime);
    }

    // Let the blackout and its recorded reply finish before Hannu asks for wood.
    if (this.hannuGate.step(
      hannuShouted,
      this.blackoutTalking || simulationTime < this.telttaUntil,
    )) {
      this.hannuUntil = simulationTime + HANNU.shoutDuration;
      this.audio.speak({ id: 'hannu', text: HANNU.shout, cue: 'hannu-shout', ...HANNU.voice });
    }

    // And Kimmo's opinion of the glass. The slow that came with it is the
    // simulation's business and has already been applied; this is only the man
    // finding out about it.
    if (this.glassDueAt !== null && simulationTime >= this.glassDueAt) {
      this.glassDueAt = null;
      this.audio.play('kimmo-shout');
    }
  }

  /**
   * Whose sentence the HUD is showing, if anybody's.
   *
   * A strict priority list rather than a queue: two people talking at once is
   * two people talking at once, and the one you want to hear is the one whose
   * hand is out. `wakeShouting` is the intro, which loses to everybody.
   */
  current(
    simulationTime: number,
    running: boolean,
    wakeShouting: boolean,
    /** Whether the plate is full, which decides which of Mäysä's two lines it is. */
    breakfastDone: boolean,
  ): { text: string; distant: boolean } | null {
    if (!running) return null;
    // This timer starts with Kimmo's recording and clears when the tent answers.
    if (this.telttaDueAt !== null) {
      return { text: KIMMO_OHO_TEXT, distant: false };
    }
    if (simulationTime < this.hannuUntil) {
      return { text: HANNU.shout, distant: false };
    }
    // The teltta above all three below it and under Hannu, and it is the only
    // one of the four whose place in this list is a real decision rather than
    // a formality. Mäysä, the cook and the porukka are each a building further
    // down the route than the cable is, so none of them can be mid-sentence
    // when this fires; Hannu can be, because the palju is twenty metres past
    // the ansalanka and that is under three seconds at the speed you leave it.
    // He wins because he has a hand out and these people are behind you.
    if (simulationTime < this.telttaUntil) {
      return { text: TELTTA_LINE.text, distant: false };
    }
    // Mäysä before the cook: he is the nearer of the two and the one who is
    // actually stopping somebody, so if both are mid-sentence his is the line
    // that matters.
    if (simulationTime < this.maysaUntil) {
      const line = breakfastDone ? MAYSA_DONE_LINE : MAYSA_LINE;
      return { text: line.text, distant: false };
    }
    if (simulationTime < this.kokkiUntil) {
      return { text: KOKKI_LINE.text, distant: false };
    }
    // The porukka cannot overlap either of the two above - they are two rooms
    // and half a minute upstream of the juhlasali's doorway - so where this
    // sits in the order is a formality rather than a decision. It is below
    // them because they are people standing in front of you and this is a room
    // shouting at you, and above the intro for the same reason the cook is.
    // Kempf above the porukka, which is the only line he can ever overlap -
    // they are seven people in the same room and everybody else is a building
    // upstream. He wins because he is answering something the player just did:
    // a press that produced somebody else's sentence would read as a press
    // that produced nothing.
    if (simulationTime < this.kempfUntil) {
      return { text: KEMPF_LINE.text, distant: false };
    }
    if (simulationTime < this.porukkaUntil) {
      return { text: PORUKKA_LINE.text, distant: false };
    }
    return wakeShouting ? { text: RUN_START_LINES[0].text, distant: true } : null;
  }
}
